import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { Badge, Button, Notice, Plate, SelectField, TextField } from "@wilco/ui";
import { useState } from "react";
import { FormPage, useDocumentForm } from "../docs/DocumentForm.tsx";
import { DocumentList } from "../docs/DocumentList.tsx";
import { formatNumber, t } from "../i18n.ts";
import { productInfo, productInfos } from "../lookups.ts";
import { ProductSearch } from "../stock/RequestDraftPage.tsx";
import { errorNotice, type NoticeState } from "../stock/ShelfPage.tsx";
import { parseQty } from "../stock/qty.ts";
import { productionApi, type Bom } from "./api.ts";
import { PRODUCTION } from "./queries.ts";

const route = getRouteApi("/app/production/boms/$productId");

const bomQuery = (productId: string) => ({ queryKey: [...PRODUCTION, "bom", productId], queryFn: () => productionApi.bom(productId), retry: false });

interface Line {
  component: string;
  qty: string;
  packing: boolean;
  /** Пусто значит строка во всех версиях. */
  versions: string[];
}

interface BomValues {
  headerVersion: string;
  lines: Line[];
}

/**
 * Спецификация изделия (CAT-09, CAT-11): строки компонентов с количеством,
 * пометкой «на пакуванні» (с линии не списывается, ВР-10) и версиями, в
 * которых строка действует; версии изделия с коэффициентом трудоёмкости
 * (CST-17). Без спецификации редактор начинает с пустой.
 */
export function BomPage() {
  const { productId } = route.useParams();
  const bom = useQuery(bomQuery(productId));
  const product = useQuery({ queryKey: ["lookups", "product", productId], queryFn: () => productInfo(productId) });
  if (bom.error) return <Notice tone="danger" title={errorNotice(bom.error).title} className="page" />;
  if (bom.data === undefined || !product.data) return null;
  return <BomEditor key={productId} productId={productId} title={`${product.data.sku} ${product.data.name}`} bom={bom.data} />;
}

function BomEditor({ productId, title, bom }: { productId: string; title: string; bom: Bom | null }) {
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState<NoticeState | null>(null);
  const versions = bom?.versions ?? [];
  const form = useDocumentForm<BomValues, Bom>({
    initial: {
      headerVersion: bom?.headerVersion ?? "",
      lines: (bom?.lines ?? []).map((l) => ({ component: l.component, qty: String(l.qty), packing: l.packing, versions: l.versions })),
    },
    validate: (v) => ({
      lines: v.lines.some((l) => (parseQty(l.qty) ?? 0) <= 0) ? t("bom.qtyInvalid") : undefined,
    }),
    submit: (v, key) =>
      productionApi.saveBom(
        productId,
        { headerVersion: v.headerVersion || null, lines: v.lines.map((l) => ({ component: l.component, qty: parseQty(l.qty) ?? 0, packing: l.packing, versions: l.versions })) },
        key,
      ),
    onSuccess: (next) => {
      queryClient.setQueryData(bomQuery(productId).queryKey, next);
      void queryClient.invalidateQueries({ queryKey: [...PRODUCTION, "boms"] });
      setSaved({ tone: "success", title: t("bom.saved") });
    },
  });
  const { values, set, errors } = form;
  const names = useQuery({ queryKey: ["lookups", "products", ...values.lines.map((l) => l.component)], queryFn: () => productInfos(values.lines.map((l) => l.component)) });
  const name = (id: string) => {
    const p = names.data?.get(id);
    return p ? `${p.sku} ${p.name}` : id;
  };
  const setLine = (i: number, patch: Partial<Line>) => {
    setSaved(null);
    set(
      "lines",
      values.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)),
    );
  };

  return (
    <FormPage
      title={t("bom.title", { product: title })}
      back="/production/boms"
      submitLabel={t("bom.save")}
      form={form}
      after={<Versions productId={productId} versions={versions} />}
    >
      {saved ? <Notice tone={saved.tone} title={saved.title} /> : null}
      {versions.length > 0 ? (
        <SelectField
          label={t("bom.headerVersion")}
          hint={t("bom.headerVersionHint")}
          value={values.headerVersion}
          onChange={(e) => set("headerVersion", e.target.value)}
          options={[{ value: "", label: "—" }, ...versions.map((v) => ({ value: v.code, label: v.code }))]}
        />
      ) : null}
      <div className="bom-lines" role="table" aria-label={t("bom.lines")}>
        {values.lines.length === 0 ? <p className="w-muted">{t("bom.noLines")}</p> : null}
        {values.lines.map((l, i) => (
          <div key={`${l.component}-${String(i)}`} className="bom-line" role="row">
            <span role="cell" className="bom-component">
              {name(l.component)}
            </span>
            <TextField label={t("bom.qty")} inputMode="decimal" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} className="bom-qty" />
            <label className="check">
              <input type="checkbox" checked={l.packing} onChange={(e) => setLine(i, { packing: e.target.checked })} />
              {t("bom.packing")}
            </label>
            {versions.length > 0 ? (
              <fieldset className="bom-versions">
                <legend className="w-field-label">{t("bom.inVersions")}</legend>
                {versions.map((v) => (
                  <label key={v.code} className="check">
                    <input
                      type="checkbox"
                      checked={l.versions.includes(v.code)}
                      onChange={(e) => setLine(i, { versions: e.target.checked ? [...l.versions, v.code] : l.versions.filter((x) => x !== v.code) })}
                    />
                    {v.code}
                  </label>
                ))}
              </fieldset>
            ) : null}
            <Button variant="ghost" type="button" onClick={() => set("lines", values.lines.filter((_, j) => j !== i))}>
              {t("bom.remove")}
            </Button>
          </div>
        ))}
      </div>
      {errors.lines ? (
        <span className="w-field-error" role="alert">
          {errors.lines}
        </span>
      ) : null}
      <ProductSearch
        onPick={(p) => {
          setSaved(null);
          set("lines", [...values.lines, { component: p.id, qty: "1", packing: false, versions: [] }]);
        }}
      />
    </FormPage>
  );
}

/** Версии изделия: код и коэффициент трудоёмкости, добавляются и убираются сразу. */
function Versions({ productId, versions }: { productId: string; versions: Bom["versions"] }) {
  const queryClient = useQueryClient();
  const [code, setCode] = useState("");
  const [coefficient, setCoefficient] = useState("1");
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const run = useMutation({ mutationFn: (fn: () => Promise<Bom>) => fn() });
  const act = (fn: () => Promise<Bom>) =>
    run.mutate(fn, {
      onSuccess: (next) => {
        queryClient.setQueryData(bomQuery(productId).queryKey, next);
        setNotice(null);
      },
      onError: (e) => setNotice(errorNotice(e)),
    });
  return (
    <section className="stock-section" aria-labelledby="bom-versions">
      <h2 id="bom-versions" className="desk-group-title">
        {t("bom.versions")}
      </h2>
      {notice ? <Notice tone={notice.tone} title={notice.title} /> : null}
      {versions.length === 0 ? <p className="w-muted">{t("bom.noVersions")}</p> : null}
      <ul className="payments">
        {versions.map((v) => (
          <li key={v.code} className="payment">
            <span className="w-num">{v.code}</span>
            <span className="payment-ref w-muted">
              {t("bom.coefficient", { value: formatNumber(v.coefficient) })}
              {v.archived ? (
                <>
                  {" "}
                  <Badge>{t("catalog.archived")}</Badge>
                </>
              ) : null}
            </span>
            <Button variant="ghost" disabled={run.isPending} onClick={() => act(() => productionApi.removeVersion(productId, v.code))}>
              {t("bom.remove")}
            </Button>
          </li>
        ))}
      </ul>
      <Plate cut={false}>
        <form
          className="qty-row bom-version-add"
          onSubmit={(e) => {
            e.preventDefault();
            if (!code.trim()) return;
            act(() => productionApi.addVersion(productId, { code: code.trim(), coefficient: parseQty(coefficient) ?? 1, archived: false }));
            setCode("");
          }}
        >
          <TextField label={t("bom.versionCode")} value={code} onChange={(e) => setCode(e.target.value)} autoComplete="off" />
          <TextField label={t("bom.versionCoefficient")} inputMode="decimal" value={coefficient} onChange={(e) => setCoefficient(e.target.value)} />
          <Button type="submit" variant="secondary" disabled={run.isPending || !code.trim()}>
            {t("bom.addVersion")}
          </Button>
        </form>
      </Plate>
    </section>
  );
}

/** «Нова специфікація»: выбор изделия, дальше редактор. */
export function NewBomPage() {
  const navigate = useNavigate();
  return (
    <div className="page stock-narrow doc-form">
      <h1 className="page-title">{t("bom.new")}</h1>
      <p className="w-muted">{t("bom.newHint")}</p>
      <ProductSearch onPick={(p) => void navigate({ to: "/production/boms/$productId", params: { productId: p.id } })} />
    </div>
  );
}

const bomsRoute = getRouteApi("/app/production/boms");

interface BomRow {
  productId: string;
  product: string;
  headerVersion: string | null;
  lines: number;
  versions: string[];
}

/**
 * «Специфікації»: изделия со спецификацией страницами сервера по курсору
 * (CursorPage), названия из справочника. Поиска и статусов у сервера нет:
 * список короткий, строка ведёт в редактор.
 */
export function BomsPage() {
  const search = bomsRoute.useSearch();
  const navigate = useNavigate();
  return (
    <DocumentList<BomRow>
      title={t("bom.list.title")}
      icon="mrp"
      queryKey={[...PRODUCTION, "boms"]}
      load={async ({ cursor, limit }) => {
        const page = await productionApi.boms(cursor, limit);
        const info = await productInfos(page.items.map((b) => b.productId));
        return {
          items: page.items.map((b) => {
            const p = info.get(b.productId);
            return { productId: b.productId, product: p ? `${p.sku} ${p.name}` : b.productId, headerVersion: b.headerVersion ?? null, lines: b.lines, versions: b.versions };
          }),
          next: page.next,
        };
      }}
      columns={[
        { key: "product", header: t("mo.product"), kind: "title", render: (b) => b.product },
        { key: "versions", header: t("bom.versions"), kind: "text", width: "12rem", render: (b) => b.versions.join(", ") || "—" },
        { key: "lines", header: t("bom.lines"), kind: "number", width: "6rem", render: (b) => formatNumber(b.lines) },
      ]}
      rowKey={(b) => b.productId}
      onOpen={(b) => void navigate({ to: "/production/boms/$productId", params: { productId: b.productId } })}
      sorts={[{ value: "product", label: t("bom.sort.product") }]}
      search={search}
      onSearch={(next) => void navigate({ to: "/production/boms", search: next, replace: true })}
      create={{ label: t("bom.new"), to: "/production/boms/new", permission: "manufacturing.order" }}
      empty={{ title: t("bom.list.empty"), body: t("bom.list.emptyBody") }}
    />
  );
}
